/**
 * AttendanceHistory
 * Aanwezigheidsoverzicht per lid met periode-filter (week/maand/kwartaal/jaar/alles).
 * Attendance wordt automatisch geregistreerd bij betreden van de meeting.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../../../firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { COLLECTIONS, ATTENDANCE_ALERT_THRESHOLD_PCT, STRINGS } from '../meetingConstants';

interface AttendanceSummary {
  userId: string;
  presenterName: string;
  presentCount: number;
  totalMeetings: number;
  pct: number;
  lastSeenMs: number | null;
}

interface AttendanceHistoryProps {
  entityId: string;
  alertThresholdPct?: number;
}

type LoadState =
  | { status: 'loading' }
  | { status: 'ready'; data: AttendanceSummary[] }
  | { status: 'error'; message: string };

type Period = 'week' | 'maand' | 'kwartaal' | 'jaar' | 'alles';

const PERIODS: { key: Period; label: string }[] = [
  { key: 'week',     label: 'Week'     },
  { key: 'maand',    label: 'Maand'    },
  { key: 'kwartaal', label: 'Kwartaal' },
  { key: 'jaar',     label: 'Jaar'     },
  { key: 'alles',    label: 'Alles'    },
];

function periodStart(period: Period): number {
  const now = new Date();
  if (period === 'week') {
    const d = new Date(now);
    d.setDate(d.getDate() - 7);
    return d.getTime();
  }
  if (period === 'maand') {
    const d = new Date(now);
    d.setMonth(d.getMonth() - 1);
    return d.getTime();
  }
  if (period === 'kwartaal') {
    const d = new Date(now);
    d.setMonth(d.getMonth() - 3);
    return d.getTime();
  }
  if (period === 'jaar') {
    const d = new Date(now);
    d.setFullYear(d.getFullYear() - 1);
    return d.getTime();
  }
  return 0; // 'alles'
}

interface RawMeetingDoc {
  entries: Array<{
    userId: string;
    presenterName: string;
    status: 'present' | 'absent';
    markedAtMs: number;
  }>;
  sessionDateMs?: number;
}

const AttendanceHistory: React.FC<AttendanceHistoryProps> = ({
  entityId,
  alertThresholdPct = ATTENDANCE_ALERT_THRESHOLD_PCT,
}) => {
  const [allDocs, setAllDocs] = useState<RawMeetingDoc[]>([]);
  const [loadState, setLoadState] = useState<LoadState>({ status: 'loading' });
  const [period, setPeriod] = useState<Period>('maand');
  const [sortKey, setSortKey] = useState<'name' | 'pct' | 'last'>('pct');
  const [sortAsc, setSortAsc] = useState(false);

  // ── Laad alle meeting-docs eenmalig ────────────────────────────────────────
  useEffect(() => {
    if (!entityId) return;
    let cancelled = false;

    const load = async (): Promise<void> => {
      try {
        const snap = await getDocs(
          query(collection(db, COLLECTIONS.ATTENDANCE), where('entityId', '==', entityId)),
        );
        const docs: RawMeetingDoc[] = snap.docs.map((d) => {
          const data = d.data();
          return {
            entries: Array.isArray(data.entries) ? data.entries : [],
            sessionDateMs: typeof data.sessionDateMs === 'number'
              ? data.sessionDateMs
              : data.updatedAt?.toMillis?.() ?? 0,
          };
        });
        if (!cancelled) {
          setAllDocs(docs);
          setLoadState({ status: 'ready', data: [] }); // data computed below via memo
        }
      } catch (err) {
        console.error('[AttendanceHistory] Load error:', err);
        if (!cancelled) setLoadState({ status: 'error', message: STRINGS.errors.generic });
      }
    };

    void load();
    return () => { cancelled = true; };
  }, [entityId]);

  // ── Aggregeer per periode ─────────────────────────────────────────────────
  const aggregated: AttendanceSummary[] = React.useMemo(() => {
    const cutoff = periodStart(period);
    const memberMap = new Map<string, AttendanceSummary>();

    allDocs.forEach((meetingDoc) => {
      const meetingTime = meetingDoc.sessionDateMs ?? 0;
      // Sla meetings buiten de periode over
      if (period !== 'alles' && meetingTime < cutoff) return;

      meetingDoc.entries.forEach((entry) => {
        const existing = memberMap.get(entry.userId);
        const isPresent = entry.status === 'present';
        if (existing) {
          memberMap.set(entry.userId, {
            ...existing,
            presentCount: existing.presentCount + (isPresent ? 1 : 0),
            totalMeetings: existing.totalMeetings + 1,
            lastSeenMs: isPresent
              ? Math.max(existing.lastSeenMs ?? 0, entry.markedAtMs ?? 0)
              : existing.lastSeenMs,
          });
        } else {
          memberMap.set(entry.userId, {
            userId: entry.userId,
            presenterName: entry.presenterName ?? 'Onbekend',
            presentCount: isPresent ? 1 : 0,
            totalMeetings: 1,
            pct: 0,
            lastSeenMs: isPresent ? (entry.markedAtMs ?? null) : null,
          });
        }
      });
    });

    return Array.from(memberMap.values()).map((m) => ({
      ...m,
      pct: m.totalMeetings > 0 ? Math.round((m.presentCount / m.totalMeetings) * 100) : 0,
    }));
  }, [allDocs, period]);

  const sorted = [...aggregated].sort((a, b) => {
    let diff = 0;
    if (sortKey === 'name') diff = a.presenterName.localeCompare(b.presenterName);
    else if (sortKey === 'pct') diff = a.pct - b.pct;
    else if (sortKey === 'last') diff = (a.lastSeenMs ?? 0) - (b.lastSeenMs ?? 0);
    return sortAsc ? diff : -diff;
  });

  const toggleSort = (key: typeof sortKey): void => {
    if (sortKey === key) setSortAsc((v) => !v);
    else { setSortKey(key); setSortAsc(false); }
  };

  // ── CSV Export ────────────────────────────────────────────────────────────
  const exportCsv = useCallback((): void => {
    if (!aggregated.length) return;
    const periodLabel = PERIODS.find((p) => p.key === period)?.label ?? period;
    const header = `Naam,Aanwezig,Totaal meetings,Percentage,Laatste bijeenkomst,Periode: ${periodLabel}`;
    const rows = aggregated.map((m) => {
      const last = m.lastSeenMs ? new Date(m.lastSeenMs).toLocaleDateString('nl-NL') : '—';
      return `${m.presenterName},${m.presentCount},${m.totalMeetings},${m.pct}%,${last}`;
    });
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `aanwezigheid_${entityId}_${period}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [aggregated, entityId, period]);

  const SortBtn: React.FC<{ col: typeof sortKey; label: string }> = ({ col, label }) => (
    <button
      onClick={() => toggleSort(col)}
      aria-label={`Sorteer op ${label}`}
      className="text-left font-semibold text-white/80 hover:text-white transition-colors"
    >
      {label} {sortKey === col ? (sortAsc ? '↑' : '↓') : ''}
    </button>
  );

  const isLoading = loadState.status === 'loading';
  const isError = loadState.status === 'error';

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-bold text-white">Aanwezigheidshistorie</h3>
        <button
          onClick={exportCsv}
          disabled={!aggregated.length}
          aria-label={STRINGS.attendance.exportLabel}
          className="min-h-[36px] px-3 rounded-lg text-[10px] font-semibold border border-white/20 bg-white/8 hover:bg-white/15 text-white transition-all disabled:opacity-40"
        >
          ↓ CSV
        </button>
      </div>

      {/* Periode-filter */}
      <div className="flex items-center gap-1 flex-wrap">
        {PERIODS.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setPeriod(key)}
            className={`px-3 py-1 rounded-full text-[10px] font-semibold transition-colors ${
              period === key
                ? 'bg-white/20 text-white border border-white/30'
                : 'bg-white/5 text-white/50 border border-white/10 hover:bg-white/10 hover:text-white/80'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Inhoud */}
      {isLoading && (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-10 rounded-xl bg-white/8 animate-pulse" />
          ))}
        </div>
      )}
      {isError && (
        <p role="alert" className="text-sm text-rose-400">{(loadState as any).message}</p>
      )}
      {!isLoading && !isError && sorted.length === 0 && (
        <p className="text-sm text-white/50 italic">
          {aggregated.length === 0 && allDocs.length === 0
            ? STRINGS.attendance.noHistory
            : 'Geen data voor deze periode.'}
        </p>
      )}
      {!isLoading && !isError && sorted.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-white/10">
          <table className="w-full text-xs text-white/85">
            <thead>
              <tr className="border-b border-white/10 text-white/50 bg-white/3">
                <th className="px-3 py-2 text-left"><SortBtn col="name" label="Naam" /></th>
                <th className="px-3 py-2 text-center"><SortBtn col="pct" label="%" /></th>
                <th className="px-3 py-2 text-center hidden sm:table-cell">Aanw.</th>
                <th className="px-3 py-2 text-right hidden md:table-cell"><SortBtn col="last" label="Laatste" /></th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((m) => {
                const isBelow = m.pct < alertThresholdPct && m.totalMeetings >= 3;
                return (
                  <tr
                    key={m.userId}
                    className={`border-b border-white/5 hover:bg-white/5 transition-colors ${isBelow ? 'bg-rose-900/15' : ''}`}
                  >
                    <td className="px-3 py-2.5 font-medium">
                      {m.presenterName}
                      {isBelow && (
                        <span
                          role="status"
                          aria-label={STRINGS.attendance.belowThreshold(m.pct)}
                          className="ml-1.5 text-rose-400"
                          title={`Aanwezigheid onder drempel (${alertThresholdPct}%)`}
                        >
                          ⚠
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-center">
                      <span className={`font-bold ${isBelow ? 'text-rose-400' : m.pct >= 80 ? 'text-emerald-400' : 'text-amber-300'}`}>
                        {m.pct}%
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-center hidden sm:table-cell text-white/50">
                      {m.presentCount}/{m.totalMeetings}
                    </td>
                    <td className="px-3 py-2.5 text-right hidden md:table-cell text-white/40">
                      {m.lastSeenMs ? new Date(m.lastSeenMs).toLocaleDateString('nl-NL') : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AttendanceHistory;
